/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import { AbstractResourceService, JsonApiResource, QueryParams } from '@irian-ro/abstract-resource';
import { HttpClient } from '@angular/common/http';
import {EnvironmentService} from "@irian-ro/util-environment";
import { AccountStatusDtoModel, CompanyDetailsDtoModel, SignupStepDto, SubscriptionVisibilityDto } from '@well/domain';
import { Observable } from 'rxjs';
import { CompanyType, LegalEntityModel } from '@well/common-libs/legal-entity-management/domain-model';

@Injectable({
  providedIn: 'root',
})
export class CompanyRegistrationResourceService extends AbstractResourceService<
  CompanyDetailsDtoModel,
  CompanyDetailsDtoModel,
  any,
  any
> {
  constructor(httpClient: HttpClient, environmentService: EnvironmentService) {
    super(
      httpClient,
      environmentService.getEnvironmentProperty('companyRegistrationUrl'),
      CompanyDetailsDtoModel,
      CompanyDetailsDtoModel
    );
  }

  getMyCompanyProfile(): Observable<JsonApiResource<CompanyDetailsDtoModel, any>> {
    return this.httpClient?.get(`${this.url}/myProfile`, { headers: this.headers }).pipe(this.getDtoMapper());
  }

  getBusinessProfileCompanyDetails(
    businessProfileUuid: string
  ): Observable<JsonApiResource<CompanyDetailsDtoModel, any>> {
    return <Observable<JsonApiResource<CompanyDetailsDtoModel, any>>>(
      this.httpClient?.get(`${this.url}/businessprofile/${businessProfileUuid}/details`, { headers: this.headers })
    );
  }

  updateValidCompanyDetails(companyDetails: CompanyDetailsDtoModel): Observable<LegalEntityModel> {
    return <Observable<LegalEntityModel>>this.httpClient?.put(`${this.url}/validate`, companyDetails);
  }

  getLastlyDoneSignupStepByCurrentUser(): Observable<JsonApiResource<SignupStepDto, any>> {
    return <Observable<JsonApiResource<SignupStepDto, any>>>this.httpClient?.get(`${this.url}/last-step`);
  }

  assignCompanyTypeToValidLegalEntity(legalEntityUuid: string, companyType: CompanyType): Observable<LegalEntityModel> {
    return <Observable<LegalEntityModel>>(
      this.httpClient?.post(`${this.url}/${legalEntityUuid}/assign-company-type/${companyType}`, {})
    );
  }

  saveCompanyDetailsAndAddSubscriptionVisibility(
    subscriptionVisibilityDto: SubscriptionVisibilityDto
  ): Observable<JsonApiResource<SubscriptionVisibilityDto, any>> {
    return <Observable<JsonApiResource<SubscriptionVisibilityDto, any>>>(
      this.httpClient?.post(`${this.url}/signup-and-add-subscription-visibility`, subscriptionVisibilityDto)
    );
  }

  addSubscriptionVisibility(
    subscriptionVisibilityDto: SubscriptionVisibilityDto
  ): Observable<JsonApiResource<SubscriptionVisibilityDto, any>> {
    return <Observable<JsonApiResource<SubscriptionVisibilityDto, any>>>(
      this.httpClient?.post(`${this.url}/add-subscription-visibility`, subscriptionVisibilityDto)
    );
  }

  createNewAccountStatusIfNotExistsForCurrentOwner(): Observable<JsonApiResource<AccountStatusDtoModel, any>> {
    return <Observable<JsonApiResource<AccountStatusDtoModel, any>>>(
      this.httpClient?.post(`${this.url}/create-new-account-status`, {})
    );
  }

  findAllLocalLegalEntityForSignup(query?: any, additionalParams?: any): Observable<JsonApiResource<LegalEntityModel[], any>> {
    const queryString = new QueryParams(query, additionalParams ? additionalParams : []).toString();
    return this.httpClient?.get(`${this.url}${queryString}`).pipe(this.mapToListOfObjects());
  }
}
